import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

const GamificationPage: React.FC = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();

    const t = (es: string, en: string) => language === 'es' ? es : en;

    const handleThemeChange = (theme: number) => {
        const bookRoutes = {
            2: '/libros/1',
            3: '/libros/2', 
            4: '/libros/3',
            5: '/libros/4',
            6: '/libros/5',
            7: '/libros/6',
            8: '/libros/7',
            9: '/libros/8'
        };
        const route = bookRoutes[theme as keyof typeof bookRoutes];
        if (route) {
            window.location.href = route;
        }
    };

    const gamificationSections = [
        {
            id: 'gamificacion',
            title: t('Gamificación', 'Gamification'),
            image: '/images/infosection/gamificacion.png',
            color: 'from-blue-500 to-cyan-600',
            description: t(
                'Sistema lúdico compuesto de juegos, reconocimientos y recompensas que impulsan y motivan al niño a aprender, emprender y mejorar de manera autónoma, dinámica y continua.',
                'Ludic system composed of games, recognitions and rewards that drive and motivate the child to learn, undertake and improve autonomously, dynamically and continuously.'
            ),
            benefits: [
                { title: t('Motivación', 'Motivation'), desc: t('Impulsa el aprendizaje autónomo', 'Drives autonomous learning') },
                { title: t('Reconocimiento', 'Recognition'), desc: t('Valida esfuerzos y logros', 'Validates efforts and achievements') },
                { title: t('Mejora Continua', 'Continuous Improvement'), desc: t('Desarrollo dinámico', 'Dynamic development') }
            ]
        },
        {
            id: 'insignias',
            title: t('Insignias y Títulos', 'Badges and Titles'),
            image: '/images/infosection/insigniastitulos.png',
            color: 'from-green-500 to-emerald-600',
            description: t(
                'Gana insignias y títulos mientras avanzas y completas las actividades impuestas en los demás componentes de nuestro sistema.',
                'Earn badges and titles as you progress and complete the activities imposed in the other components of our system.'
            ),
            benefits: [
                { title: t('Progreso', 'Progress'), desc: t('Avance estructurado', 'Structured advancement') },
                { title: t('Logros', 'Achievements'), desc: t('Completar actividades', 'Complete activities') },
                { title: t('Reconocimiento', 'Recognition'), desc: t('Títulos y insignias', 'Titles and badges') }
            ]
        },
        {
            id: 'juegos',
            title: t('Juegos y Mobile App', 'Games and Mobile App'),
            image: '/images/infosection/juegosmobiles.png',
            color: 'from-purple-500 to-pink-600',
            description: t(
                'Diviértete y completa los juegos y retos que tenemos preparados en diferentes elementos de nuestro sistema; además de nuestra mobile app donde aprenderás a debatir y competir con otros pequeños sabios a nivel global.',
                'Have fun and complete the games and challenges we have prepared in different elements of our system; plus our mobile app where you will learn to debate and compete with other little sages globally.'
            ),
            benefits: [
                { title: t('Diversión', 'Fun'), desc: t('Juegos y retos', 'Games and challenges') },
                { title: t('Debate', 'Debate'), desc: t('Aprende a argumentar', 'Learn to argue') },
                { title: t('Competencia Global', 'Global Competition'), desc: t('Compite internacionalmente', 'Compete internationally') }
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
            {/* Navbar */}
            <Navbar currentTheme={1} onThemeChange={handleThemeChange} />
            
            {/* Contenido principal */}
            <main className="pt-16">
                {/* Secciones de gamificación */}
                {gamificationSections.map((section, index) => (
                    <section key={section.id} className="min-h-screen relative">
                        {section.id === 'gamificacion' ? (
                            // Diseño especial para la primera sección (Gamificación)
                            <div className="relative z-10 flex items-center justify-center min-h-screen">
                                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                                        {/* Columna izquierda - Imagen */}
                                        <div className="relative">
                                            <div className="w-full h-[600px] rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center shadow-2xl p-8">
                                                <img 
                                                    src={section.image}
                                                    alt={section.title}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            
                                            {/* Elementos decorativos */}
                                            <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full animate-pulse"></div>
                                            <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-pink-400 rounded-full animate-bounce"></div>
                                            <div className="absolute top-1/2 -left-2 w-4 h-4 bg-green-400 rounded-full animate-pulse"></div>
                                        </div>
                                        
                                        {/* Columna derecha - Contenido */}
                                        <div className="text-center lg:text-left">
                                            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-8">
                                                {section.title}
                                            </h2>
                                            
                                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-2xl">
                                                <p className="text-xl sm:text-2xl text-gray-800 leading-relaxed">
                                                    {section.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Diseño original para las otras secciones
                            <>
                                {/* Imagen de fondo */}
                                <div className="absolute inset-0 w-full h-full">
                                    <img 
                                        src={section.image}
                                        alt={section.title}
                                        className="w-full h-full object-cover object-center"
                                    />
                                </div>
                                
                                {/* Contenido centrado */}
                                <div className="relative z-10 flex items-center justify-center min-h-screen">
                                    <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${index % 2 === 0 ? 'transform translate-x-44' : 'transform -translate-x-44'} ${section.id === 'juegos' ? 'transform translate-x-60 -translate-y-20' : ''} ${section.id === 'ecosistema' ? 'self-start pt-16' : ''} ${section.id === 'compensacion' ? 'self-start pt-15 transform translate-x-60' : ''} ${section.id === 'financiamiento' ? 'self-start pt-19 transform -translate-x-60' : ''}`}>
                                        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8">
                                            {section.title}
                                        </h2>
                                        
                                        {/* Mostrar cuadro solo si no es ecosistema */}
                                        {section.id !== 'ecosistema' && (
                                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 sm:p-12 shadow-2xl">
                                                <p className="text-xl sm:text-2xl text-gray-800 leading-relaxed">
                                                    {section.description}
                                                </p>
                                                
                                                {/* Mostrar tarjetas de beneficios solo si no es la primera sección (Gamificación) */}
                                                {section.id !== 'gamificacion' && section.id !== 'insignias' && section.id !== 'juegos' && section.id !== 'compensacion' && section.id !== 'financiamiento' && (
                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
                                                        {section.benefits.map((benefit, benefitIndex) => (
                                                            <div key={benefitIndex} className={`bg-gradient-to-br ${section.color} rounded-xl p-6 text-white`}>
                                                                <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                                                                <p className="text-sm opacity-90">{benefit.desc}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </section>
                ))}
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default GamificationPage; 